import React, { useState } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { InfoCircleIcon, CrossIcon } from '@vlabs/icons';
import { selectListOptions } from '@vlabs/shared/selectors/listSelectors';
import validate from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';
import type { TOption, WithDataTestid } from '@vlabs/uikit/types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './MoveListForm.module.sass';

interface TMoveListFormValues {
  listId: string | TOption;
}

interface MoveListFormProps extends WithDataTestid {
  onSubmit: (listId: string) => Promise<void>;
  onCancel: VoidFunction;
}

export function MoveListForm({
  'data-testid': dataTestId = 'MoveListForm',
  onSubmit,
  onCancel,
}: MoveListFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const lists = useSelector(selectListOptions);

  const [mode, setMode] = useState<'select' | 'create'>('select');

  const {
    control,
    register,
    watch,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<TMoveListFormValues>({ defaultValues: { listId: '' } });
  const listValue = watch('listId');
  const testid = buildTestid(dataTestId);

  const submitForm = async () => {
    if (mode === 'create') {
      try {
        const createResponse = await apiContainer.lunaClient.lists.create({
          user_data: listValue,
        });

        await onSubmit(createResponse.data.list_id);
      } catch {
        toast.error(t('features:move_cards_to_list.move_list_form.toast.create_list_error'));
      }

      return;
    }

    await onSubmit(listValue.value);
  };

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <div className={st.Root}>
        <div className={st.Header}>
          <span className={st.Title}>
            {t('features:move_cards_to_list.move_list_form.title')}
          </span>
          <span className={st.Subtitle}>
            <InfoCircleIcon className={st.SubtitleIcon} />
            {t('features:move_cards_to_list.move_list_form.subtitle')}
          </span>
        </div>

        <div className={st.ListsOptions}>
          <Control.Select
            control={control}
            disabled={mode === 'create'}
            name="listId"
            options={lists}
            placeholder={t('features:move_cards_to_list.move_list_form.select.placeholder')}
            {...testid('ExistingListsSelect')}
          />
          {mode === 'select' && (
            <Control.Button
              className={st.CreateListButton}
              kind="primary"
              onClick={() => {
                setMode('create');
                reset({ listId: '' });
              }}
              variant="flat"
              {...testid('CreateListSwitchModeButton')}
            >
              {t('features:move_cards_to_list.move_list_form.buttons.new_list')}
            </Control.Button>
          )}
          {mode === 'create' && (
            <div className={st.CreateNewList}>
              <div className={st.InputWrapper}>
                <Control.Input
                  {...register('listId', { maxLength: validate.maxLength(128) })}
                  errors={errors}
                  label={t('features:move_cards_to_list.move_list_form.input.label')}
                  placeholder={t('features:move_cards_to_list.move_list_form.input.placeholder')}
                  {...testid('CreateNewListInput')}
                />
              </div>
              <Control.IconButton
                className={st.IconButton}
                icon={<CrossIcon />}
                kind="negative"
                onClick={() => {
                  setMode('select');
                  reset({ listId: '' });
                }}
                variant="flat"
                {...testid('SwitchModeButton')}
              />
            </div>
          )}
        </div>

        <div className={st.Actions}>
          <Control.Button
            className={st.Action}
            disabled={!listValue}
            kind="primary"
            type="submit"
            variant="filled"
            {...testid('SubmitButton')}
          >
            {t('features:move_cards_to_list.move_list_form.buttons.submit')}
          </Control.Button>
          <Control.Button
            className={st.Action}
            kind="secondary"
            onClick={onCancel}
            variant="outlined"
            {...testid('CloseButton')}
          >
            {t('features:move_cards_to_list.move_list_form.buttons.cancel')}
          </Control.Button>
        </div>
      </div>
    </form>
  );
}
